import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Close as CloseIcon,
  PointOfSale,
  Restaurant,
  Tune,
  Home,
  BarChart as ReportsIcon,
  Kitchen as KitchenIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CommandPalette from "./CommandPalette";
import MobileBottomNav from "./MobileBottomNav";
import { EMPLOYEE_TYPES } from "../../appConst";
import { useUserWithCompany } from "../../hooks/useCompany";
import CompanyConfigurationServices from "../../services/CompanyConfigurationService";

const ModernLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const { companyId } = useUserWithCompany();
  const isPOSRoute = location.pathname.startsWith("/pos");

  console.log("user", user);

  const [anchorEl, setAnchorEl] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [companyModules, setCompanyModules] = useState({
    POS: true,
    Kitchen: true,
  });
  const [modulesLoading, setModulesLoading] = useState(true);

  // Get user type label
  const getUserTypeLabel = () => {
    if (!user?.type) return "User";
    const employeeType = EMPLOYEE_TYPES.find(
      (type) => type.value === user.type,
    );
    return employeeType ? employeeType.label : user.type;
  };

  // Check if user has access to a navigation item
  const hasAccess = (allowedRoles) => {
    if (!user?.type) return true; // Default access if no user type

    // SuperAdmin, RestaurantAdmin, admin, and manager have full access
    if (
      user.type === "SuperAdmin" ||
      user.type === "RestaurantAdmin" ||
      user.type === "admin" ||
      user.type === "manager"
    ) {
      return true;
    }

    // Check if user's type is in the allowed roles
    return allowedRoles.includes(user.type);
  };

  // Keyboard shortcut for command palette (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (!companyId) {
      setModulesLoading(false);
      return;
    }

    CompanyConfigurationServices.getConfigurations(companyId, "compnay_modules")
      .then((response) => {
        if (!response?.success) return;

        const items =
          response?.data?.view?.data ??
          response?.data?.view?.items ??
          response?.data?.items ??
          [];

        if (items.length > 0) {
          const value = items[0]?.value || {};
          setCompanyModules({
            POS: value.POS !== false,
            Kitchen: value.Kitchen !== false,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching company modules for layout:", error);
      })
      .finally(() => {
        setModulesLoading(false);
      });
  }, [companyId]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate("/profile");
  };

  const handleLogout = () => {
    dispatch({ type: "LOGOUT" });
    localStorage.clear();
    handleMenuClose();
    navigate("/login");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: isPOSRoute ? "100vh" : "auto",
        minHeight: isPOSRoute ? "100vh" : "100vh",
        overflow: isPOSRoute ? "hidden" : "visible",
      }}
    >
      {/* Modern Top App Bar - Premium Design */}
      {!isPOSRoute && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
            backdropFilter: "blur(30px)",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            color: theme.palette.text.primary,
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.3 : 0.08)}`,
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: 56, md: 78 },
              px: { xs: 1.5, md: 3, lg: 4 },
              gap: { xs: 1, md: 2 },
            }}
          >
            {/* Logo / Brand - Enhanced */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                mr: { xs: 1, md: 3 },
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => navigate("/dashboard")}
            >
              <Box
                component="img"
                src="/assets/logo.png"
                alt="Menux"
                sx={{
                  height: { xs: 24, md: 30 },
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Quick Navigation - Modern Tab Style - Centered */}
            <Box sx={{ flexGrow: 1 }} />
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  p: 0.75,
                  borderRadius: "12px",
                  background: alpha(
                    theme.palette.common.black,
                    theme.palette.mode === "dark" ? 0.2 : 0.06,
                  ),
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                }}
              >
                {[
                  {
                    label: "Home",
                    path: "/dashboard",
                    icon: <Home sx={{ fontSize: 18 }} />,
                    allowedRoles: ["admin", "manager", "cashier", "waiter"],
                  },
                  {
                    label: "POS",
                    path: "/pos",
                    icon: <PointOfSale sx={{ fontSize: 18 }} />,
                    allowedRoles: ["admin", "manager", "cashier", "waiter"],
                  },
                  {
                    label: "Public Menu",
                    path: "/restaurant-menu",
                    icon: <Restaurant sx={{ fontSize: 18 }} />,
                    openInNewTab: true,
                    allowedRoles: ["admin", "manager", "cashier", "waiter"],
                  },
                  {
                    label: "KDS",
                    path: "/kds",
                    icon: <KitchenIcon sx={{ fontSize: 18 }} />,
                    allowedRoles: ["kitchen_staff"],
                  },
                  {
                    label: "Settings",
                    path: "/settings",
                    icon: <Tune sx={{ fontSize: 18 }} />,
                    allowedRoles: ["admin", "manager"],
                  },
                  {
                    label: "Reports",
                    path: "/reports",
                    icon: <ReportsIcon sx={{ fontSize: 18 }} />,
                    allowedRoles: ["admin", "manager", "cashier"],
                  },
                ]
                  .filter((item) => hasAccess(item.allowedRoles))
                  .filter((item) => {
                    if (item.path === "/pos") {
                      return !modulesLoading && companyModules.POS;
                    }
                    if (item.path === "/kds") {
                      return !modulesLoading && companyModules.Kitchen;
                    }
                    return true;
                  })
                  .map((item) => {
                    const isActive = location.pathname.includes(item.path);
                    return (
                      <Box
                        key={item.path}
                        onClick={() =>
                          item.openInNewTab
                            ? window.open(item.path, "_blank")
                            : navigate(item.path)
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 2,
                          py: 1,
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          background: isActive
                            ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                            : "transparent",
                          color: isActive
                            ? "#ffffff"
                            : theme.palette.text.primary,
                          boxShadow: isActive
                            ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                            : "none",
                          "&:hover": {
                            background: isActive
                              ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                              : alpha(theme.palette.primary.main, 0.08),
                            transform: "translateY(-2px)",
                            boxShadow: isActive
                              ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                              : `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                          },
                        }}
                      >
                        {item.icon}
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            letterSpacing: "-0.2px",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    );
                  })}
              </Box>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* User Type Display — desktop only */}
            <Chip
              label={getUserTypeLabel()}
              size="medium"
              sx={{
                display: { xs: "none", md: "flex" },
                mr: 1.5,
                px: 1,
                height: 36,
                fontWeight: 600,
                fontSize: "0.875rem",
                borderRadius: "10px",
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                border: `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                color: theme.palette.primary.main,
                backdropFilter: "blur(10px)",
                transition: "all 0.3s ease",
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                },
              }}
            />

            {/* Notifications - Enhanced */}
            {/* <Tooltip title="Notifications" arrow>
              <IconButton
                sx={{
                  mr: 0.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.1)",
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip> */}

            {/* Theme Toggle - Enhanced */}
            {/* <Tooltip title={isDarkMode ? "Light Mode" : "Dark Mode"} arrow>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  mr: 0.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.1) rotate(20deg)",
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                {isDarkMode ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Tooltip>  */}

            {/* User Avatar - Enhanced */}
            <Tooltip title="Account" arrow>
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{
                  p: 0.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.15)",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    fontWeight: 600,
                    fontSize: "1rem",
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                  }}
                >
                  {user?.firstName ? user.firstName[0].toUpperCase() : "U"}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Toolbar>

          {/* Mobile Search Overlay - Enhanced */}
          <AnimatePresence>
            {isMobile && searchOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Box
                  sx={{
                    px: 2,
                    pb: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      background: alpha(
                        theme.palette.common.black,
                        theme.palette.mode === "dark" ? 0.2 : 0.06,
                      ),
                      borderRadius: "12px",
                      px: 2,
                      py: 1.2,
                      cursor: "pointer",
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      transition: "all 0.2s ease",
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                    }}
                    onClick={() => {
                      setSearchOpen(false);
                      setCommandPaletteOpen(true);
                    }}
                  >
                    <SearchIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, color: "text.secondary", fontWeight: 500 }}
                    >
                      Search...
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchOpen(false);
                      }}
                      sx={{
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1,
                          ),
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </AppBar>
      )}

      {/* Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />

      {/* User Menu - Premium Design */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 220,
            borderRadius: "12px",
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            background:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.95)
                : alpha(theme.palette.background.paper, 0.98),
            backdropFilter: "blur(20px)",
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.3 : 0.1)}`,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ letterSpacing: "-0.3px" }}
          >
            {user?.firstName || "User"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {user?.email || "user@email.com"}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={handleProfile}
          sx={{
            py: 1.2,
            px: 2,
            mx: 1,
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <AccountCircle sx={{ mr: 2, fontSize: 20, color: "primary.main" }} />
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate("/data-setup");
          }}
          sx={{
            py: 1.2,
            px: 2,
            mx: 1,
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <SettingsIcon sx={{ mr: 2, fontSize: 20, color: "primary.main" }} />
          Settings
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 1.2,
            px: 2,
            mx: 1,
            borderRadius: "8px",
            color: "error.main",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.1),
            },
          }}
        >
          Logout
        </MenuItem>
      </Menu>

      {/* Mobile bottom navigation */}
      {!isPOSRoute && (
        <MobileBottomNav
          navItems={[
            { label: "Home", icon: <Home sx={{ fontSize: 22 }} />, path: "/dashboard" },
            { label: "Reports", icon: <ReportsIcon sx={{ fontSize: 22 }} />, path: "/reports" },
            { label: "Settings", icon: <Tune sx={{ fontSize: 22 }} />, path: "/settings" },
          ].filter((item) => {
            const roleMap = {
              "/dashboard": ["admin", "manager", "cashier", "waiter"],
              "/reports": ["admin", "manager", "cashier"],
              "/settings": ["admin", "manager"],
            };
            return hasAccess(roleMap[item.path] || []);
          })}
        />
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: isPOSRoute ? 0 : { xs: 7, md: 9 },
          px: isPOSRoute ? 0 : { xs: 1, md: 2 },
          py: isPOSRoute ? 0 : { xs: 1.5, md: 2 },
          pb: isPOSRoute ? 0 : { xs: "84px", md: 2 },
          backgroundColor: theme.palette.background.default,
          height: isPOSRoute ? "100vh" : "auto",
          minHeight: isPOSRoute ? "100vh" : "calc(100vh - 56px)",
          overflow: isPOSRoute ? "hidden" : "visible",
        }}
      >
        {children}
      </Box>

      {/* Optional Footer */}
      {!isPOSRoute && (
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 4,
            mt: "auto",
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            display="block"
          >
            © {new Date().getFullYear()} Restaurant Management System. All
            rights reserved.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ModernLayout;
